import { AccessStamp } from "../components/AccessStamp";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button, Card, ErrorNote, Notice, Spinner } from "../components/primitives";
import { api } from "../lib/api";
import { canAfford, reservesFor, shortfall } from "../lib/credit";
import { money } from "../lib/format";
import { modelLabel } from "../lib/labels";
import { pricedFor } from "../lib/pricing";
import type { BriefField, DataRoomSummary, Depth, InputSpec, Playbook } from "../lib/types";

/**
 * The engagement brief, for one playbook.
 *
 * Every field on the `Engagement` model is reachable here — the form is the
 * API surface, not a simplified subset of it.
 *
 * **The playbook comes from the route, and words its own brief.** It used to
 * be a dropdown on one shared form, which asked every product the same
 * questions: "Target" for an industry scan collects a company name and the
 * whole scan then researches the wrong thing. Each playbook now declares what
 * to call these fields, which it needs and which it has no use for, in its
 * `playbook.yaml`.
 *
 * Declared rather than eight hand-written pages, because a playbook is a
 * directory and adding one must not mean writing code — a rule this would
 * otherwise break one layer above Python. An operator's own playbook from
 * `$CA_PLAYBOOK_PATH` gets its form for the same reason.
 */

/** What a field is called and how it behaves, before the playbook speaks. */
const DEFAULTS: Record<BriefField, InputSpec> = {
  target: {
    label: "Target",
    hint: null,
    placeholder: "Company or asset under review",
    required: true,
    hidden: false,
  },
  sector: {
    label: "Sector",
    hint: null,
    placeholder: "Left blank, the agent establishes it",
    required: false,
    hidden: false,
  },
  geography: {
    label: "Geographic scope",
    hint: null,
    placeholder: null,
    required: false,
    hidden: false,
  },
  client: {
    label: "Commissioned by",
    hint: null,
    placeholder: "internal",
    required: false,
    hidden: false,
  },
  thesis: {
    label: "Investment thesis to test",
    hint: null,
    placeholder:
      "State it the way the client states it. The agent tests each pillar and says which hold.",
    required: false,
    hidden: false,
  },
  data_room: {
    label: "Data room",
    hint: null,
    placeholder: null,
    // Never from the declaration: `requires_data_room` is the only thing that
    // makes a room mandatory, and `fieldOf` reads it off the playbook.
    required: false,
    hidden: false,
  },
  questions: {
    label: "Specific questions — one per line",
    hint: null,
    placeholder:
      "Is the 15% growth assumption defensible?\nHow concentrated is revenue?",
    required: false,
    hidden: false,
  },
  // Hidden unless a playbook asks for it. A mandate and a prompt are two ways
  // of saying what the work is, and a form offering both asks the person to
  // say it twice.
  prompt: {
    label: "What do you want done?",
    hint: "In your own words. Say what you want produced, and in what form.",
    placeholder:
      "Size the European market for industrial bearings and show the three largest segments as a chart.",
    required: false,
    hidden: true,
  },
};

/** The declaration over the default. An absent key means "as always". */
function fieldOf(book: Playbook | undefined, name: BriefField): InputSpec {
  const base = DEFAULTS[name];
  const said = book?.inputs?.[name];
  if (!said) return base;
  return {
    label: said.label ?? base.label,
    hint: said.hint ?? base.hint,
    placeholder: said.placeholder ?? base.placeholder,
    // `target` is required whatever a playbook says — a run has to be about
    // something, and the loader refuses to hide it for the same reason.
    required: name === "target" ? true : said.required || base.required,
    hidden: name === "target" ? false : said.hidden,
  };
}

/**
 * Where the data room field belongs for this playbook.
 *
 * It used to be one of two states — inside the collapsed "Advanced" card, or
 * pulled out and starred when `requires_data_room`. Only `review-cim` requires
 * one, so for every other playbook the field was behind a disclosure titled
 * "Advanced" that gave no hint a data room was in it. `cdd` was the case that
 * showed the cost: its own `lead.md` and `phase_scope.md` tell the agent to
 * read a data room, the backend has always accepted one, and a user could not
 * find where to say so.
 *
 * Three states now, from two orthogonal declarations:
 *
 * - `requires_data_room: true` — shown, starred, card titled for it.
 * - `inputs: { data_room: … }` — shown, optional. The playbook is saying it
 *   uses documents well.
 * - neither — inside Advanced, exactly as before, so nothing regresses.
 *
 * `hidden` removes it in all three, for a playbook that takes a company name
 * and nothing else.
 */
/**
 * The data room picker, placed by `roomPlacement`.
 *
 * Its own component because it is rendered in two places now — in a card of
 * its own when the playbook asks for documents, and inside "Advanced" when it
 * does not. One copy, so the unlock prompt and the wording cannot diverge
 * between them.
 */
function DataRoomField({
  labelled = true,
  spec,
  needsRoom,
  value,
  onChange,
  rooms,
  chosen,
  onUnlocked,
}: {
  /** False where a card already titles the field, so it is not said twice. */
  labelled?: boolean;
  spec: InputSpec;
  needsRoom: boolean;
  value: string;
  onChange: (id: string) => void;
  rooms: DataRoomSummary[];
  chosen: DataRoomSummary | undefined;
  onUnlocked: () => void;
}) {
  return (
                <div>
                  <label
                    className={labelled ? "label" : "sr-only"}
                    htmlFor="data-room"
                  >
                    {spec.label}{needsRoom && " *"}
                  </label>
                  {spec.hint && (
                    <p className="mb-1.5 text-xs leading-relaxed text-muted">
                      {spec.hint}
                    </p>
                  )}
                  <select
                    id="data-room"
                    className="field"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  >
                    <option value="">
                      {needsRoom
                        ? "Choose the data room holding the memo"
                        : "None — outside-in review"}
                    </option>
                    {(rooms).map((room) => (
                      <option key={room.id} value={room.id}>
                        {room.name} ({room.files} file{room.files === 1 ? "" : "s"}
                        {room.locked?.length
                          ? `, ${room.locked.length} locked`
                          : ""}
                        )
                      </option>
                    ))}
                  </select>
                  {/*
                    Said here because here is where it can still be fixed. A
                    locked CIM does not fail the run — it produces a well-formed
                    review of a company the agent knows only from the web, which
                    is the failure `requires_data_room` exists to prevent one
                    step earlier and reads as a bad answer rather than a missing
                    document.
                  */}
                  {chosen?.locked?.length ? (
                    <UnlockBeforeStart
                      room={chosen}
                      onUnlocked={() => onUnlocked()}
                    />
                  ) : null}
                  <p className="mt-1.5 text-xs text-muted">
                    Client documents outrank third-party estimates. Upload one on
                    the{" "}
                    <Link to="/data-rooms" className="underline">
                      documents
                    </Link>{" "}
                    page — it stays private to you. Expert interviews live there
                    too and reach a run through memory, not through this field.
                  </p>
                </div>
  );
}

function roomPlacement(
  book: Playbook | undefined,
): "required" | "offered" | "advanced" | "hidden" {
  const spec = fieldOf(book, "data_room");
  if (spec.hidden) return "hidden";
  if (book?.requires_data_room) return "required";
  return book?.inputs?.data_room ? "offered" : "advanced";
}

/**
 * One brief field, worded by the playbook.
 *
 * A textarea when `rows` is given, an input otherwise — the difference is
 * whether the answer is a name or an argument, which is a property of the
 * field and not of the playbook.
 */
function Field({
  name,
  book,
  value,
  onChange,
  wide = false,
  rows,
  autoFocus = false,
}: {
  name: BriefField;
  book: Playbook | undefined;
  value: string;
  onChange: (next: string) => void;
  wide?: boolean;
  rows?: number;
  autoFocus?: boolean;
}) {
  const spec = fieldOf(book, name);
  if (spec.hidden) return null;

  const common = {
    id: name,
    className: "field",
    value,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
    placeholder: spec.placeholder ?? undefined,
    required: spec.required,
  };

  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <label className="field-label" htmlFor={name}>
        {spec.label}
        {spec.required && " *"}
      </label>
      {rows ? (
        <textarea {...common} className="field resize-y" rows={rows} />
      ) : (
        <input {...common} autoFocus={autoFocus} />
      )}
      {spec.hint && (
        <p className="mt-1.5 text-xs leading-relaxed text-muted">{spec.hint}</p>
      )}
    </div>
  );
}

/** "a", "a and b", "a, b and c". */
function sentenceList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function NewEngagement() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const route = useParams();

  // Arrived from "Run" on a planned project: bind this engagement to it rather
  // than creating a second record for the same piece of work.
  const projectId = params.get("project")
    ? Number(params.get("project"))
    : null;
  const linked = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api.project(projectId!),
    enabled: projectId !== null,
  });
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  const toolServers = useQuery({ queryKey: ["tools"], queryFn: api.tools });
  // Data rooms only. An interview room holds transcripts, which reach a run
  // through the knowledge graph rather than by being mounted as a directory —
  // offering one here would attach a folder the prompt then calls "client
  // documents". Asked for by kind server-side, so the filter is not the
  // client's to get wrong.
  const dataRooms = useQuery({
    queryKey: ["data-rooms", "data_room"],
    queryFn: () => api.dataRooms("data_room"),
  });
  const credit = useQuery({ queryKey: ["credits"], queryFn: api.credits });
  // Shares the cache with the header, so this costs no extra request.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  const [target, setTarget] = useState("");
  // Prefilled from a landing tile, which is where the visitor made this choice
  // — carried through sign-in in the session and handed back on the redirect.
  // `cdd` when there is nothing to carry, as before.
  // From the route. `?playbook=` is still read because a pending account is
  // sent back here with its remembered choice, and older links carry it.
  const playbook = route.playbook ?? params.get("playbook") ?? "cdd";
  const [sector, setSector] = useState("");
  const [geography, setGeography] = useState("Global");
  const [thesis, setThesis] = useState("");
  const [prompt, setPrompt] = useState("");
  const [client, setClient] = useState("");
  const [questions, setQuestions] = useState("");
  const [depth, setDepth] = useState<Depth>("standard");
  // Empty means "the provider's default", which is also what the API and CLI
  // do — so switching provider does not need the form to know model names.
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [subagentModel, setSubagentModel] = useState("");
  const [dataRoom, setDataRoom] = useState("");
  const [maxRevisions, setMaxRevisions] = useState(2);
  const [isolateMemory, setIsolateMemory] = useState(false);
  // Kept as a string so an empty box means "use the depth default" rather
  // than 0, which would read as an unspendable run.
  const [maxCost, setMaxCost] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Prefill from the linked project once, then leave the form alone so typing
  // is never overwritten by a refetch.
  useEffect(() => {
    if (!linked.data || prefilled) return;
    setTarget((current) => current || linked.data.target || linked.data.name);
    setClient((current) => current || linked.data.client || "");
    setPrefilled(true);
  }, [linked.data, prefilled]);

  // Arrived from a mail that found no playbook for a request
  // (`?from=<proposal>`): the question becomes the instruction and the mail's
  // subject the run's name. The proposal is the asker's own — anybody else's
  // is a 404 and the form stays empty. Once, like the project above.
  const from = params.get("from");
  const asked = useQuery({
    queryKey: ["proposal", from],
    queryFn: () => api.proposal(from!),
    enabled: !!from,
    retry: false,
  });
  const [prefilledAsk, setPrefilledAsk] = useState(false);
  useEffect(() => {
    if (!asked.data || prefilledAsk) return;
    setPrompt((current) => current || asked.data.request);
    setTarget((current) => current || asked.data.subject.replace(/^(re|aw|fwd?):\s*/i, "").slice(0, 80));
    setPrefilledAsk(true);
  }, [asked.data, prefilledAsk]);

  // Resolved rather than stored: the provider list arrives with the options
  // query, so an empty selection falls through to the server's default instead
  // of the form having to guess one before the query lands.
  const providerList = options.data?.providers ?? [];
  const activeProvider =
    providerList.find((p) => p.key === provider) ??
    providerList.find((p) => p.key === options.data?.default_provider) ??
    providerList[0];
  const providerModels = activeProvider?.models ?? [];
  const modelUnavailable =
    model !== "" && !providerModels.some((m) => m.id === model);

  const chooseProvider = (key: string) => {
    setProvider(key);
    // Model ids do not carry across providers, so a leftover choice would be
    // sent to an endpoint that has never heard of it. Reset to the default.
    setModel("");
    setSubagentModel("");
  };

  // Every depth's ceiling, asked of the server for the models chosen — a
  // ceiling is priced by its models, and the rule lives in one place. Each
  // pick is a new key and so a new answer; the last one stays on screen
  // until it lands, so the tiles never blank while a model is changed.
  const prompted = prompt.trim() !== "";
  const ceilings = useQuery({
    queryKey: [
      "ceilings",
      playbook,
      activeProvider?.key ?? provider,
      model,
      subagentModel.trim(),
      prompted,
    ],
    queryFn: () =>
      api.ceilings({
        playbook,
        provider: activeProvider?.key ?? provider,
        model,
        subagentModel: subagentModel.trim(),
        prompted,
      }),
    // The provider resolves with the options; asking before would ask twice.
    enabled: options.isSuccess,
    placeholderData: keepPreviousData,
  });
  const ceilingOf = (key: Depth): number | undefined =>
    ceilings.data?.ceiling_usd[key] ??
    options.data?.depths.find((d) => d.key === key)?.default_cost_usd;

  const start = useMutation({
    mutationFn: () =>
      api.start({
        target: target.trim(),
        playbook,
        sector: sector.trim() || null,
        geography: geography.trim() || "Global",
        thesis: thesis.trim() || null,
        prompt: prompt.trim() || null,
        client: client.trim() || null,
        questions: questions
          .split("\n")
          .map((q) => q.trim())
          .filter(Boolean),
        depth,
        provider: activeProvider?.key ?? provider,
        model,
        subagent_model: subagentModel.trim() || null,
        data_room_id: dataRoom || null,
        max_revisions: maxRevisions,
        isolate_memory: isolateMemory,
        max_cost_usd: maxCost.trim() === "" ? null : Number(maxCost),
        project_id: projectId,
      }),
    onSuccess: (job) => navigate(`/engagements/${encodeURIComponent(job.run_id)}`),
  });

  if (playbooks.isLoading || options.isLoading) return <Spinner />;

  const selected = playbooks.data?.find((p) => p.key === playbook);

  // A key that is not in the registry. Said here rather than left to the 400
  // the POST would return, because the person has a mistyped or stale URL and
  // the fix is to pick from the list.
  const unknown = playbooks.data !== undefined && selected === undefined;

  // A playbook that reviews a document you supply cannot start without one.
  // The server refuses it regardless — this is so the form asks for it rather
  // than offering a run that would only 400 after the button is pressed.
  const chosenRoom = (dataRooms.data ?? []).find((room) => room.id === dataRoom);
  const placement = roomPlacement(selected);
  const needsRoom = placement === "required";
  const roomMissing = needsRoom && !dataRoom;
  const roomSpec = fieldOf(selected, "data_room");

  // A playbook may declare a field required — `cdd` its thesis, `key-questions`
  // its questions. The button has to reflect the same gate the form enforces,
  // or it invites a click that the browser then refuses.
  const values: Record<BriefField, string> = {
    target,
    sector,
    geography,
    client,
    thesis,
    questions,
    prompt,
    // The room's id rather than prose, and it is never `required` here: the
    // loader refuses that on the declaration, so this can only ever be false.
    // `roomMissing` is the gate for a playbook that needs one.
    data_room: dataRoom,
  };
  const missing = (Object.keys(values) as BriefField[]).filter(
    (name) => fieldOf(selected, name).required && !values[name].trim(),
  );
  const depthInfo = options.data?.depths.find((d) => d.key === depth);

  // Both rules live in `lib/credit.ts` as pure functions, tested against the
  // same cases `tests/test_credits.py` asserts. They were inlined here until
  // the admin exemption drifted from the API's and nothing caught it — a
  // predicate in a 621-line component cannot be reached without a DOM.
  const reserves = reservesFor(
    maxCost,
    depth,
    ceilings.data?.reserves_usd,
    ceilingOf(depth),
  );
  const isAdmin = me.data?.role === "admin";
  const exempt = isAdmin;
  const affordable = canAfford(me.data?.role, credit.data?.available_usd, reserves);

  const sourcesTotal = (toolServers.data ?? []).length;
  const sourcesAvailable = (toolServers.data ?? []).filter((t) => t.available).length;

  //     Why Start cannot be pressed, said beside it — one reason, the first
  //     that applies, in the order somebody would fix them.
  const missingLabels = missing.map((name) => (fieldOf(selected, name).label ?? name).toLowerCase());
  const blocked: ReactNode | null =
    missing.length > 0 ? (
      `Add ${sentenceList(missingLabels)} to start.`
    ) : roomMissing ? (
      <>
        {selected?.title} reviews a document you supply. Upload it on the{" "}
        <Link to="/data-rooms" className="underline">
          Documents
        </Link>{" "}
        page, then choose that data room above.
      </>
    ) : !affordable && credit.data ? (
      <>
        Not enough credit. Top up by{" "}
        <span className="font-mono tabular-nums">
          {money(shortfall(credit.data.available_usd, reserves))}
        </span>{" "}
        to start this.
      </>
    ) : null;

  if (unknown) {
    return (
      <div className="max-w-read space-y-4">
        <h1 className="text-lg font-semibold">No such playbook</h1>
        <Card title={playbook}>
          <p className="p-4 text-sm leading-relaxed text-ink-2">
            There is no playbook with that key. It may have been renamed, or
            supplied by an operator and since removed.{" "}
            <Link to="/" className="text-accent underline">
              Pick one from the list
            </Link>
            .
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-read space-y-4">
      <h1 className="text-lg font-semibold">New engagement</h1>

      {linked.data && (
        <div className="card flex flex-wrap items-center gap-2 px-4 py-3 text-sm">
          <span className="text-ink-2">Running for project</span>
          <span className="font-medium">{linked.data.name}</span>
          <span className="text-xs text-muted">
            {linked.data.client ?? "internal"} · {linked.data.project_type}
          </span>
          <Link
            to="/projects"
            className="ml-auto text-xs text-accent underline underline-offset-2"
          >
            not this one?
          </Link>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (missing.length === 0) start.mutate();
        }}
        className="space-y-4"
      >
        <Card
          title={selected ? selected.title : "Mandate"}
          action={
            selected && (
              <div className="flex items-center gap-2">
                <AccessStamp access={selected.access} />
                <Link to="/" className="text-xs text-accent">
                  Change
                </Link>
              </div>
            )
          }
        >
          {selected && (
            <p className="border-b border-line px-4 py-3 text-xs leading-relaxed text-ink-2">
              {selected.description}
            </p>
          )}
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <Field
              name="target"
              book={selected}
              value={target}
              onChange={setTarget}
              wide
              autoFocus
            />
            <Field name="sector" book={selected} value={sector} onChange={setSector} />
            <Field
              name="geography"
              book={selected}
              value={geography}
              onChange={setGeography}
            />
            <Field name="client" book={selected} value={client} onChange={setClient} />
            <Field
              name="prompt"
              book={selected}
              value={prompt}
              onChange={setPrompt}
              wide
              rows={6}
            />
            <Field
              name="thesis"
              book={selected}
              value={thesis}
              onChange={setThesis}
              wide
              rows={5}
            />
            <Field
              name="questions"
              book={selected}
              value={questions}
              onChange={setQuestions}
              wide
              rows={4}
            />
          </div>
        </Card>

        {(placement === "required" || placement === "offered") && (
          <Card
            // The playbook's own word for the field, once. It read "Client
            // documents — optional" over a label saying CLIENT DOCUMENTS (#124).
            title={`${roomSpec.label} — ${needsRoom ? "required" : "optional"}`}
          >
            <div className="grid gap-4 p-4 sm:grid-cols-2">
              <DataRoomField
                labelled={false}
                spec={roomSpec}
                needsRoom={needsRoom}
                value={dataRoom}
                onChange={setDataRoom}
                rooms={dataRooms.data ?? []}
                chosen={chosenRoom}
                onUnlocked={() => dataRooms.refetch()}
              />
            </div>
          </Card>
        )}

        <Card title="Depth and spend">
          <div className="p-4">
            <div className="grid gap-2 sm:grid-cols-3">
              {options.data?.depths.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setDepth(option.key)}
                  aria-pressed={depth === option.key}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    depth === option.key
                      ? "border-accent bg-accent-soft/25"
                      : "border-line hover:bg-surface-2"
                  }`}
                >
                  <span className="block text-sm font-medium capitalize">
                    {option.key}
                  </span>
                  <span className="tabular mt-0.5 block text-xs text-muted">
                    up to {option.research_turns} research turns
                  </span>
                  <span className="tabular mt-0.5 block text-xs text-muted">
                    ~${(ceilingOf(option.key) ?? option.default_cost_usd).toFixed(0)} ceiling
                  </span>
                </button>
              ))}
            </div>
            {/* Why the figures above moved when a model was picked. */}
            {pricedFor(ceilings.data, options.data) && (
              <p className="mt-2 text-xs text-muted">{pricedFor(ceilings.data, options.data)}</p>
            )}
            {depthInfo && (
              <p className="mt-3 text-sm text-ink-2">{depthInfo.guidance}</p>
            )}

            <div className="mt-4 border-t border-line pt-4">
              <label className="field-label" htmlFor="max-cost">
                Spend ceiling (USD)
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="max-cost"
                  type="number"
                  min={1}
                  // `step={5}` made the browser refuse everything that was not
                  // 1, 6, 11 … — so 45, 50 and 60 were all "Gültigen Wert
                  // eingeben" with nothing on screen saying why. A step is a
                  // constraint, not a nudge; the arrows are not worth it.
                  step="any"
                  className="field tabular w-40"
                  value={maxCost}
                  placeholder={ceilingOf(depth)?.toFixed(0) ?? ""}
                  onChange={(e) => setMaxCost(e.target.value)}
                />
                {maxCost.trim() !== "" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setMaxCost("")}
                  >
                    Use the {depth} default
                  </Button>
                )}
              </div>
              <p className="mt-1.5 text-xs text-muted">
                The run stops here and keeps every finding it has recorded. A
                specialist is capped at {depthInfo?.subagent_turns} turns of its
                own, so a research fan-out cannot spend without limit behind a
                single delegation.
              </p>
            </div>
          </div>
        </Card>

        {/*
          Everything a first run can leave as it is, behind one disclosure —
          the model, the data sources, the knowledge base, revision rounds.
          They used to be two full cards between the brief and the spend, and
          the data sources card held the operator's setup instructions (#124).
          Collapsed, the card still says what it is set to, and a provider that
          withholds tools is said here whether it is open or not.
        */}
        <Card
          title="Advanced"
          action={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={showAdvanced}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? "Hide" : "Show"}
            </Button>
          }
        >
          {!showAdvanced && (
            <p className="px-4 py-3 text-xs text-ink-2">
              {[
                activeProvider?.label,
                modelLabel(options.data, activeProvider?.key, model || activeProvider?.default_model),
                `${sourcesAvailable} of ${sourcesTotal} data sources`,
                isolateMemory ? "walled off from other clients" : "shared knowledge base",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {activeProvider && activeProvider.unsupported.length > 0 && (
            <div className="px-4 pb-3">
              <p className="flex gap-2 rounded-lg border border-line-strong px-3 py-2 text-xs">
                <span
                  aria-hidden
                  className="mt-1 h-2 w-2 shrink-0 rounded-full bg-warning"
                />
                <span>
                <span className="font-medium">
                  {activeProvider.label} does not serve:{" "}
                  {activeProvider.unsupported.join(", ")}.
                </span>{" "}
                Those tools are withheld for this run rather than granted and
                left to fail on every call. The run still completes; it
                completes with less. Pair it with a data room, or use
                Anthropic where the missing capability matters.
                </span>
              </p>
            </div>
          )}
          {showAdvanced && (
            <div className="divide-y divide-line">
              <section aria-labelledby="advanced-model">
                <h3 id="advanced-model" className="px-4 pt-4 text-sm font-semibold text-ink">
                  Model
                </h3>
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  <div>
                    <label className="field-label" htmlFor="provider">
                      Provider
                    </label>
                    <select
                      id="provider"
                      className="field"
                      value={activeProvider?.key ?? ""}
                      onChange={(e) => chooseProvider(e.target.value)}
                    >
                      {providerList.map((p) => (
                        <option key={p.key} value={p.key} disabled={!p.configured}>
                          {p.label}
                          {p.configured
                            ? ""
                            : isAdmin
                              ? ` — set ${p.api_key_env}`
                              : " — not available here"}
                        </option>
                      ))}
                    </select>
                    {activeProvider?.note && (
                      <p className="mt-1.5 text-xs text-muted">{activeProvider.note}</p>
                    )}
                  </div>

                  <div>
                    <label className="field-label" htmlFor="model">
                      Lead model
                    </label>
                    <select
                      id="model"
                      className="field"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                    >
                      <option value="">
                        Provider default
                        {activeProvider ? ` (${activeProvider.default_model})` : ""}
                      </option>
                      {providerModels.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    {modelUnavailable && (
                      <p className="mt-1.5 text-xs text-ink-2">
                        {activeProvider?.label} does not list {model}. It will be sent
                        as given — the catalog is a menu, not an allowlist.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="field-label" htmlFor="subagent-model">
                      Subagent model
                    </label>
                    <select
                      id="subagent-model"
                      className="field"
                      value={subagentModel}
                      onChange={(e) => setSubagentModel(e.target.value)}
                    >
                      <option value="">Same as lead</option>
                      {providerModels.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-muted">
                      Research fans out to five specialists — a cheaper model here is
                      the main cost lever.
                    </p>
                  </div>

                </div>
              </section>

              <section aria-labelledby="advanced-sources">
                <h3
                  id="advanced-sources"
                  className="flex items-baseline justify-between px-4 pt-4 text-sm font-semibold text-ink"
                >
                  Data sources
                  <span className="text-xs font-normal text-muted">
                    <span className="font-mono tabular-nums">
                      {sourcesAvailable} of {sourcesTotal}
                    </span>{" "}
                    available
                  </span>
                </h3>
                {/* Shown before the run, not after: knowing the agent had no access
                    to filings changes how you read the report. */}
                <ul className="divide-y divide-line">
                  {(toolServers.data ?? []).map((server) => (
                    <li key={server.key} className="flex items-start gap-3 px-4 py-2.5">
                      <span
                        className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                          server.available ? "bg-good" : "bg-baseline"
                        }`}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-baseline gap-2">
                          <span className="text-sm font-medium">{server.title}</span>
                          {/* "not installed" was wrong for two of the four: the
                              websearch server is installed and merely unconfigured,
                              and an unset EU_FILINGS_MCP_PATH is not an
                              installation problem either. */}
                          <span className="text-xs text-muted">
                            {server.available
                              ? "available"
                              : isAdmin
                                ? "not configured"
                                : "not available here"}
                          </span>
                        </span>
                        <span className="mt-0.5 block text-xs text-ink-2">
                          {server.description}
                        </span>
                        {/* The concrete reasons, which the API has always sent as
                            `missing` and this page never showed — only the generic
                            hint. "BRAVE_API_KEY is not set" is actionable in a way
                            that a paragraph of prose about three backends is not. */}
                        {/* How to fix it is the operator's business: environment
                            variables, a `git clone`, a self-test command. A deal
                            team starting a run cannot act on any of it, and it was
                            the longest text on their form (#124). */}
                        {isAdmin && !server.available && server.missing.length > 0 && (
                          <span className="mt-1 block text-xs text-ink-2">
                            {server.missing.join(" · ")}
                          </span>
                        )}
                        {isAdmin && !server.available && server.install_hint && (
                          <span className="mt-1 block text-xs text-muted">
                            {server.install_hint}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="px-4 py-2.5 text-xs text-muted">
                  Every installed server is offered to the specialists it suits. Data
                  they return is cited as <code>server/tool</code> and verified against
                  the calls actually made.
                </p>
              </section>

              <section aria-label="Knowledge base and revisions">
                <div className="grid gap-4 p-4 sm:grid-cols-2">
                  {placement === "advanced" && (
                  <DataRoomField
                    spec={roomSpec}
                    needsRoom={needsRoom}
                    value={dataRoom}
                    onChange={setDataRoom}
                    rooms={dataRooms.data ?? []}
                    chosen={chosenRoom}
                    onUnlocked={() => dataRooms.refetch()}
                  />
                  )}

                  {/*
                    Said before the run starts, not buried in documentation. The
                    graph is shared by design — reuse across engagements is the
                    whole feature — but "my findings become visible to everyone
                    here" is not something a person should discover afterwards.
                  */}
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="isolate-memory">
                      Knowledge base
                    </label>
                    <label className="mt-1 flex items-start gap-2.5 text-sm">
                      <input
                        id="isolate-memory"
                        type="checkbox"
                        className="mt-0.5"
                        checked={isolateMemory}
                        onChange={(e) => setIsolateMemory(e.target.checked)}
                      />
                      <span className="text-ink-2">
                        Wall this engagement off from other clients&rsquo; work.
                      </span>
                    </label>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted">
                      {isolateMemory ? (
                        <>
                          This run will not read findings recorded against other
                          clients, and what it learns from documents and interviews
                          stays with this client.
                        </>
                      ) : selected?.access === "private" ? (
                        <>
                          <strong className="text-ink-2">
                            {selected.title} is a private playbook.
                          </strong>{" "}
                          It reads the shared knowledge base, but what it records
                          stays with you — no other user's engagement will see its
                          findings. Nothing to tick: the playbook decides this, not
                          the run.
                        </>
                      ) : (
                        <>
                          By default this run <strong className="text-ink-2">reads
                          and contributes to the shared knowledge base</strong>, so
                          what it establishes about a company or a market becomes
                          visible to everyone in this workspace. That reuse is the
                          point of it — but if the mandate is confidential from
                          colleagues, tick the box.
                        </>
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="field-label" htmlFor="max-revisions">
                      Max revision rounds
                    </label>
                    <input
                      id="max-revisions"
                      type="number"
                      min={0}
                      max={4}
                      className="field"
                      value={maxRevisions}
                      onChange={(e) => setMaxRevisions(Number(e.target.value))}
                    />
                    <p className="mt-1.5 text-xs text-muted">
                      How many times a blocking red-team verdict can send the draft back.
                    </p>
                  </div>
                </div>
              </section>
            </div>
          )}
        </Card>

        {start.isError && <ErrorNote error={start.error} />}

        {/*
          What this run will reserve and the button that spends it, pinned to
          the bottom of the window. Start used to sit under ~2,600px of form,
          greyed out with no reason beside it (#124). The reason is the line
          next to it now: what is missing, or what credit is short.
        */}
        <div className="sticky bottom-0 z-10 border-t border-line bg-plane py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button type="submit" variant="primary" disabled={blocked !== null || start.isPending}>
              {start.isPending ? "Starting…" : "Start engagement"}
            </Button>
            <div className="min-w-0 flex-1 text-xs leading-relaxed">
              {credit.data && (
                <p className="text-ink-2">
                  Reserves up to{" "}
                  <span className="font-mono tabular-nums text-ink">{money(reserves)}</span>
                  {exempt ? (
                    <>
                      . As an admin you are not held to your balance (
                      <span className="font-mono tabular-nums">
                        {money(credit.data.available_usd)}
                      </span>
                      ); this run may take it below zero.
                    </>
                  ) : affordable ? (
                    <>
                      {" "}of{" "}
                      <span className="font-mono tabular-nums">
                        {money(credit.data.available_usd)}
                      </span>{" "}
                      available. You are charged what the run costs; the rest is
                      released.
                    </>
                  ) : (
                    <>
                      , and{" "}
                      <span className="font-mono tabular-nums">
                        {money(credit.data.available_usd)}
                      </span>{" "}
                      is available.
                    </>
                  )}
                </p>
              )}
              {/* What is missing, in ink beside a button that cannot be
                  pressed yet. It was orange from the first paint, which
                  scolded an empty form for being empty. */}
              <p className={blocked ? "text-ink-2" : "text-muted"}>
                {blocked ?? "Runs take from minutes to hours. You can close this tab — progress is written to disk."}
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

/**
 * Unlock a data room's encrypted documents without leaving the form.
 *
 * The person starting the engagement is the one holding the password — it came
 * in the mail with the CIM — and this is the moment they are thinking about
 * that room. Sending them to the documents page to come back would lose the
 * form they have half filled in.
 *
 * **Warned, not blocked.** A room can legitimately hold one locked appendix
 * among twenty readable files, and refusing to start would be this system
 * deciding which of the client's documents matter. The consequence is stated
 * plainly instead, which is the same posture as the shared-memory notice above.
 *
 * The password is sent, used once to convert the document, and stored nowhere.
 */
function UnlockBeforeStart({
  room,
  onUnlocked,
}: {
  room: DataRoomSummary;
  onUnlocked: () => void;
}) {
  const [password, setPassword] = useState("");
  const unlock = useMutation({
    mutationFn: async () => {
      for (const file of room.locked) {
        await api.unlockDataRoomFile(room.id, file, password);
      }
    },
    onSuccess: () => {
      setPassword("");
      onUnlocked();
    },
  });

  return (
    // The one thing on this form somebody has to act on, so it takes the
    // orange — as the dot, with the words in ink on a neutral ground.
    <Notice tone="attention" compact className="mt-2">
      <div>
        {room.locked.length === 1 ? (
          <>
            <span className="font-mono">{room.locked[0]}</span> is
            password-protected and has not been read.
          </>
        ) : (
          <>
            {room.locked.length} documents in this room are password-protected
            and have not been read.
          </>
        )}{" "}
        A run starting now would not see what they say.
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="password"
          value={password}
          autoComplete="off"
          placeholder="Document password"
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            // Enter here must not submit the engagement form around it.
            if (e.key === "Enter") {
              e.preventDefault();
              if (password) unlock.mutate();
            }
          }}
          className="field field-sm w-48"
        />
        <Button
          type="button"
          size="sm"
          disabled={!password || unlock.isPending}
          onClick={() => unlock.mutate()}
        >
          {unlock.isPending ? "Unlocking…" : "Unlock"}
        </Button>
      </div>
      {unlock.error && <ErrorNote error={unlock.error} />}
    </Notice>
  );
}
